import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"
import { Snackbar, Alert, AlertColor } from "@mui/material"

// Define the context type
interface SnackbarContextType {
  showSnackbar: (message: string, severity?: AlertColor) => void
}

// Create the context with an initial undefined value
const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
)

// Custom hook to use the Snackbar context
// eslint-disable-next-line react-refresh/only-export-components
export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider")
  }
  return context
}

// Define the Snackbar state type
interface SnackbarState {
  open: boolean
  message: string
  severity: AlertColor
}

// Define props for the provider
interface SnackbarProviderProps {
  children: ReactNode
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  })

  const showSnackbar = useCallback(
    (message: string, severity: AlertColor = "info") => {
      setSnackbar({ open: true, message, severity })
    },
    []
  )

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason !== "clickaway") {
      setSnackbar((prev) => ({ ...prev, open: false }))
    }
  }

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleClose}
        sx={{
          position: "fixed",
          zIndex: 9999,
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}
